import { createApi } from '@reduxjs/toolkit/query/react';

import { STATES_WHERE_ONBOARDING_IS_NOT_ALLOWED } from 'constants/onboarding';
import { baseQuery } from 'utils/services';

import { Option } from 'models/forms.types';
import { MembershipPlan } from 'models/plans.types';

import {
  GetAddressAutocompleteReqProps,
  GetAddressAutocompleteResProps,
  GetAppointmentCategoriesReqProps,
  GetAppointmentCategoriesResProps,
  GetAppointmentTypesReqProps,
  GetAppointmentTypesResProps,
  GetFrontDeskTypesDataItemProps,
  GetFrontDeskTypesReqProps,
  GetFrontDeskTypesResProps,
  GetMembershipPlansReqProps,
  GetMembershipPlansResProps,
  GetStatesReqProps,
  GetStatesResProps,
  GetSuggestedAddressesReqProps,
  GetSuggestedAddressesResProps
} from './lookup.types';

export const lookupApi = createApi({
  baseQuery,
  endpoints: (build) => ({
    getAddressAutocomplete: build.query<
      GetAddressAutocompleteResProps,
      GetAddressAutocompleteReqProps
    >({
      query: ({ search }) => ({
        params: { search },
        url: '/lookup/address-autocomplete'
      })
    }),
    getAppointmentCategories: build.query<
      GetAppointmentCategoriesResProps,
      GetAppointmentCategoriesReqProps
    >({
      query: (accessToken) => ({
        headers: {
          Authorization: accessToken || undefined
        },
        url: '/lookup/appointment-categories'
      })
    }),
    getAppointmentTypes: build.query<GetAppointmentTypesResProps, GetAppointmentTypesReqProps>({
      keepUnusedDataFor: 3600, // 1 hour
      query: ({ accessToken, ...params }) => ({
        headers: {
          Authorization: accessToken
        },
        params: { sortField: 'displayName', sortOrder: 'ASC', ...params },
        url: '/lookup/appointment-types'
      })
    }),
    getFrontDeskTypes: build.query<GetFrontDeskTypesDataItemProps[], GetFrontDeskTypesReqProps>({
      query: () => ({
        url: '/lookup/front-desk-request-types'
      }),
      transformResponse: (response: GetFrontDeskTypesResProps) => response.data
    }),
    getMembershipPlans: build.query<MembershipPlan[], GetMembershipPlansReqProps>({
      keepUnusedDataFor: 3600, // 1 hour
      query: (params) => ({
        params: {
          limit: 50,
          ...params
        },
        url: '/lookup/membership-plans'
      }),
      transformResponse: (response: GetMembershipPlansResProps) => response.data
    }),
    getStates: build.query<Option[], GetStatesReqProps>({
      query: (params) => ({
        ...(params?.hideStatesWithoutLicense && {
          params: { hideStatesWithoutLicense: true }
        }),
        url: '/lookup/state-list'
      }),
      transformResponse: (response: GetStatesResProps) =>
        response.data
          .map((v) => ({
            label: v.name,
            value: v.stateCode
          }))
          .filter((v) => !STATES_WHERE_ONBOARDING_IS_NOT_ALLOWED.includes(v.label))
    }),
    getSuggestedAddresses: build.query<
      GetSuggestedAddressesResProps,
      GetSuggestedAddressesReqProps
    >({
      query: (params) => ({
        params,
        url: '/lookup/verify-addresses'
      })
    })
  }),
  reducerPath: 'lookupApi',
  tagTypes: ['Lookup']
});

export const {
  useGetAppointmentTypesQuery,
  useGetFrontDeskTypesQuery,
  useGetMembershipPlansQuery,
  useGetStatesQuery,
  useLazyGetAddressAutocompleteQuery,
  useLazyGetAppointmentTypesQuery,
  useLazyGetMembershipPlansQuery,
  useLazyGetSuggestedAddressesQuery
} = lookupApi;
